/**
 * intakeSchema.js — the Project / Brand Intake.
 *
 * Answered once per project. Two jobs:
 *   1. Fields marked `brandKey` auto-fill the bracketed placeholders in every template.
 *   2. Fields marked `drivesPlan` feed the recommendation engine in lib/agreements/recommend.js,
 *      which turns the answers into a required / recommended / situational document plan.
 *
 * Field types: text | textarea | select | multiselect | radio
 * Every option value is a stable machine key — labels can be reworded without breaking the rules.
 */

export const INTAKE_SECTIONS = [
  {
    id: "brand",
    title: "Brand & product",
    blurb: "The basics. These also fill in the blanks in every template you generate.",
    fields: [
      {
        id: "legalEntityName",
        label: "Legal entity name",
        type: "text",
        placeholder: "Acme Beverage Co., LLC",
        help: "Exactly as it appears on your formation filing. Leave blank if you have not formed yet.",
        brandKey: true,
        token: "[COMPANY NAME]",
      },
      {
        id: "tradeName",
        label: "Brand name",
        type: "text",
        placeholder: "Acme",
        help: "What consumers see on the package.",
        brandKey: true,
        token: "[BRAND NAME]",
        required: true,
      },
      {
        id: "entityType",
        label: "Entity type",
        type: "select",
        options: [
          { value: "none", label: "Not formed yet" },
          { value: "llc", label: "LLC" },
          { value: "corporation", label: "Corporation" },
          { value: "other", label: "Something else / not sure" },
        ],
        default: "none",
        drivesPlan: true,
      },
      {
        id: "stateOfFormation",
        label: "State of formation",
        type: "text",
        placeholder: "Delaware",
        brandKey: true,
        // The token the templates actually declare. [STATE OF INCORPORATION] appears in none of
        // them, so pointing here left the form blank while the document filled itself in.
        token: "[COMPANY STATE]",
      },
      {
        id: "principalAddress",
        label: "Principal business address",
        type: "textarea",
        placeholder: "123 Main St, Suite 400, Austin, TX 78701",
        brandKey: true,
        // Likewise: the templates use [COMPANY ADDRESS]; [ADDRESS] is used by none of them.
        token: "[COMPANY ADDRESS]",
      },
      {
        id: "productCategory",
        label: "Product category",
        type: "select",
        options: [
          { value: "beverage", label: "Beverage" },
          { value: "energy-drink", label: "Energy drink" },
          { value: "functional-beverage", label: "Functional beverage" },
          { value: "food", label: "Food" },
          { value: "snack", label: "Snack" },
          { value: "candy", label: "Candy / confection" },
          { value: "supplement", label: "Supplement" },
          { value: "wellness", label: "Wellness product" },
          { value: "beauty", label: "Beauty / personal care" },
          { value: "other", label: "Other packaged consumer product" },
        ],
        default: "beverage",
        brandKey: true,
        token: "[PRODUCT CATEGORY]",
        drivesPlan: true,
        required: true,
      },
      {
        id: "stage",
        label: "Where are you right now?",
        type: "select",
        options: [
          { value: "idea", label: "Idea — nothing built yet" },
          { value: "development", label: "Development — formulating" },
          { value: "pre-launch", label: "Pre-launch — lining up production" },
          { value: "manufacturing", label: "Manufacturing — producing" },
          { value: "launch", label: "Launch — first sales" },
          { value: "distribution", label: "Distribution — distributors and retail" },
          { value: "growth", label: "Growth — scaling" },
          { value: "fundraising", label: "Fundraising" },
          { value: "exit", label: "Exploring a sale" },
        ],
        default: "development",
        drivesPlan: true,
        required: true,
      },
    ],
  },

  {
    id: "ownership",
    title: "Ownership & people",
    blurb: "Who owns the company, and who is doing the work.",
    fields: [
      {
        id: "founderCount",
        label: "How many founders?",
        type: "select",
        options: [
          { value: "solo", label: "Just me" },
          { value: "two-plus", label: "Two or more" },
        ],
        default: "solo",
        drivesPlan: true,
      },
      {
        id: "equityPromised",
        label: "Have you promised equity to anyone?",
        type: "radio",
        options: [
          { value: "no", label: "No" },
          { value: "verbally", label: "Verbally, nothing signed" },
          { value: "documented", label: "Yes, and it is documented" },
        ],
        default: "no",
        drivesPlan: true,
      },
      {
        id: "team",
        label: "Who is working on the brand?",
        type: "multiselect",
        options: [
          { value: "employees", label: "W-2 employees" },
          { value: "contractors", label: "Independent contractors / freelancers" },
          { value: "consultants", label: "Consultants" },
          { value: "advisors", label: "Advisors" },
          { value: "sales-reps", label: "Commission sales reps" },
          { value: "demo-staff", label: "Demo / sampling staff" },
          { value: "none", label: "Just the founders" },
        ],
        default: [],
        drivesPlan: true,
      },
      {
        id: "founderIpAssigned",
        label: "Has every founder assigned their work and IP to the company in writing?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes, signed" },
          { value: "no", label: "No" },
          { value: "unsure", label: "Not sure" },
        ],
        default: "unsure",
        drivesPlan: true,
      },
    ],
  },

  {
    id: "product",
    title: "Product & manufacturing",
    blurb: "How the product gets made, and who owns what it is made of.",
    fields: [
      {
        id: "formulationBy",
        label: "Who develops the formula?",
        type: "multiselect",
        options: [
          { value: "in-house", label: "In-house / the founders" },
          { value: "food-scientist", label: "A food scientist or formulator" },
          { value: "lab", label: "A product development lab" },
          { value: "flavor-house", label: "A flavor house" },
          { value: "co-packer", label: "The co-packer" },
          { value: "not-started", label: "Not started yet" },
        ],
        default: [],
        drivesPlan: true,
      },
      {
        id: "formulaOwnership",
        label: "Do you own your formula in writing?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes, in a signed document" },
          { value: "no", label: "No" },
          { value: "unsure", label: "Not sure" },
          { value: "na", label: "No formula yet" },
        ],
        default: "unsure",
        drivesPlan: true,
      },
      {
        id: "manufacturingModel",
        label: "How is the product manufactured?",
        type: "select",
        options: [
          { value: "not-yet", label: "Not manufacturing yet" },
          { value: "co-packer", label: "Third-party co-packer" },
          { value: "contract-manufacturer", label: "Contract manufacturer" },
          { value: "private-label", label: "Private label" },
          { value: "own-facility", label: "Our own facility" },
        ],
        default: "not-yet",
        drivesPlan: true,
      },
      {
        id: "suppliesInputs",
        label: "Who buys the ingredients and packaging?",
        type: "select",
        options: [
          { value: "we-do", label: "We do" },
          { value: "manufacturer", label: "The manufacturer" },
          { value: "mixed", label: "Some of each" },
          { value: "unknown", label: "Not decided" },
        ],
        default: "unknown",
        drivesPlan: true,
      },
      {
        id: "packagingTypes",
        label: "Packaging formats",
        type: "multiselect",
        options: [
          { value: "cans", label: "Cans" },
          { value: "bottles", label: "Bottles" },
          { value: "labels", label: "Labels" },
          { value: "cartons", label: "Cartons / cases" },
          { value: "pouches", label: "Pouches" },
          { value: "jars", label: "Jars / tubs" },
          { value: "boxes", label: "Folding cartons / boxes" },
        ],
        default: [],
        drivesPlan: true,
      },
    ],
  },

  {
    id: "channels",
    title: "How you sell",
    blurb: "Every channel brings its own contract.",
    fields: [
      {
        id: "channels",
        label: "Which channels are live or planned in the next 12 months?",
        type: "multiselect",
        options: [
          { value: "dtc", label: "Own website / DTC" },
          { value: "subscription", label: "Subscription program" },
          { value: "amazon", label: "Amazon or other marketplace" },
          { value: "retail-direct", label: "Selling direct to retailers" },
          { value: "distributor", label: "Through a distributor" },
          { value: "dsd", label: "DSD (direct store delivery)" },
          { value: "broker", label: "Through a sales broker" },
          { value: "foodservice", label: "Foodservice" },
          { value: "international", label: "International" },
        ],
        default: [],
        drivesPlan: true,
        required: true,
      },
      {
        id: "exclusivityAsked",
        label: "Has any distributor asked for exclusivity?",
        type: "radio",
        options: [
          { value: "no", label: "No" },
          { value: "asked", label: "Yes, it has come up" },
          { value: "signed", label: "We already signed something" },
        ],
        default: "no",
        drivesPlan: true,
      },
      {
        id: "logistics",
        label: "Where does inventory sit?",
        type: "select",
        options: [
          { value: "none", label: "No inventory yet" },
          { value: "3pl", label: "A 3PL" },
          { value: "warehouse", label: "A rented warehouse" },
          { value: "copacker", label: "At the co-packer" },
          { value: "self", label: "We store it ourselves" },
        ],
        default: "none",
        drivesPlan: true,
      },
      {
        id: "coldChain",
        label: "Does the product need temperature control?",
        type: "radio",
        options: [
          { value: "no", label: "No" },
          { value: "yes", label: "Yes — refrigerated or frozen" },
          { value: "seasonal", label: "Seasonally (heat-sensitive)" },
        ],
        default: "no",
        drivesPlan: true,
      },
    ],
  },

  {
    id: "marketing",
    title: "Marketing & brand",
    blurb: "Who makes your creative, and who is allowed to use it.",
    fields: [
      {
        id: "marketingHelp",
        label: "Who helps with marketing and creative?",
        type: "multiselect",
        options: [
          { value: "agency", label: "A marketing or ad agency" },
          { value: "designer", label: "A packaging or graphic designer" },
          { value: "photographer", label: "A photographer or videographer" },
          { value: "influencers", label: "Influencers / creators" },
          { value: "ugc", label: "UGC creators" },
          { value: "ambassadors", label: "Brand ambassadors" },
          { value: "affiliates", label: "An affiliate program" },
          { value: "none", label: "Nobody yet" },
        ],
        default: [],
        drivesPlan: true,
      },
      {
        id: "creativeOwnership",
        label: "Do you have signed assignments for your logo, packaging artwork and photography?",
        type: "radio",
        options: [
          { value: "yes", label: "Yes, all of it" },
          { value: "partial", label: "Some of it" },
          { value: "no", label: "No" },
          { value: "unsure", label: "Not sure" },
        ],
        default: "unsure",
        drivesPlan: true,
      },
      {
        id: "licensing",
        label: "Any licensing or partnership deals?",
        type: "multiselect",
        options: [
          { value: "inbound", label: "We license someone else's brand or name" },
          { value: "outbound", label: "We license our brand to someone" },
          { value: "celebrity", label: "A celebrity, athlete or personality is involved" },
          { value: "cobrand", label: "A co-branded product" },
          { value: "none", label: "None" },
        ],
        default: [],
        drivesPlan: true,
      },
      {
        id: "eventsSampling",
        label: "Do you run demos, sampling, contests or sweepstakes?",
        type: "multiselect",
        options: [
          { value: "demos", label: "In-store demos" },
          { value: "sampling", label: "Event sampling" },
          { value: "sponsorship", label: "Event sponsorships" },
          { value: "contest", label: "Contests or sweepstakes" },
          { value: "none", label: "None" },
        ],
        default: [],
        drivesPlan: true,
      },
    ],
  },

  {
    id: "money",
    title: "Money",
    blurb: "Capital raised, capital planned, and debt.",
    fields: [
      {
        id: "raised",
        label: "Have you raised outside money?",
        type: "multiselect",
        options: [
          { value: "none", label: "No outside money" },
          { value: "friends-family", label: "Friends and family" },
          { value: "angels", label: "Angel investors" },
          { value: "safe-note", label: "SAFEs or convertible notes" },
          { value: "priced", label: "A priced equity round" },
          { value: "strategic", label: "A strategic investor (distributor, retailer, co-packer)" },
        ],
        default: [],
        drivesPlan: true,
      },
      {
        id: "planningRaise",
        label: "Planning to raise in the next 12 months?",
        type: "radio",
        options: [
          { value: "no", label: "No" },
          { value: "yes", label: "Yes" },
          { value: "maybe", label: "Maybe" },
        ],
        default: "no",
        drivesPlan: true,
      },
      {
        id: "debt",
        label: "Any debt or financing?",
        type: "multiselect",
        options: [
          { value: "none", label: "None" },
          { value: "bank-loan", label: "Bank loan or line of credit" },
          { value: "founder-loan", label: "A founder or shareholder loan" },
          { value: "equipment", label: "Equipment financing" },
          { value: "inventory", label: "Inventory or PO financing" },
          { value: "rbf", label: "Revenue-based financing" },
        ],
        default: [],
        drivesPlan: true,
      },
      {
        id: "exploringSale",
        label: "Are you exploring a sale of the brand or company?",
        type: "radio",
        options: [
          { value: "no", label: "No" },
          { value: "someday", label: "Someday, not now" },
          { value: "active", label: "Yes, actively" },
        ],
        default: "no",
        drivesPlan: true,
      },
    ],
  },
];

/** Flat field list. */
export const INTAKE_FIELDS = INTAKE_SECTIONS.flatMap((s) => s.fields.map((f) => ({ ...f, section: s.id })));
export const INTAKE_FIELD_BY_ID = Object.fromEntries(INTAKE_FIELDS.map((f) => [f.id, f]));

/** Fields that feed template placeholders, as { fieldId -> token }. */
export const BRAND_TOKEN_MAP = Object.fromEntries(
  INTAKE_FIELDS.filter((f) => f.brandKey && f.token).map((f) => [f.id, f.token]),
);

/** An empty intake with every default applied. */
export const EMPTY_INTAKE = Object.fromEntries(
  INTAKE_FIELDS.map((f) => [f.id, f.type === "multiselect" ? [...(f.default || [])] : f.default ?? ""]),
);

export const REQUIRED_INTAKE_FIELDS = INTAKE_FIELDS.filter((f) => f.required).map((f) => f.id);

/**
 * Has the founder actually told us this field, or is it still sitting on the schema default?
 *
 * Most fields carry a default so the controls have something to render (a Radix Select cannot
 * hold an empty value). A default is NOT an answer: counting it as one made a founder who had
 * opened nothing read as "54% answered" with a plan they never asked for, and made every
 * "you have not started yet" branch in the module unreachable.
 */
export function isAnsweredField(field, value) {
  if (!field) return false;
  const def = field.type === "multiselect" ? field.default || [] : field.default ?? "";
  if (Array.isArray(value)) {
    const base = Array.isArray(def) ? def : [];
    if (value.length === 0) return false;
    if (value.length !== base.length) return true;
    const sorted = [...value].map(String).sort();
    const baseSorted = [...base].map(String).sort();
    return sorted.some((v, i) => v !== baseSorted[i]);
  }
  const v = String(value ?? "").trim();
  if (v === "") return false;
  return v !== String(def ?? "").trim();
}

/**
 * 0–100 — how much of the intake the founder has actually answered.
 * Multiselects count as answered when at least one option is chosen; a field still holding its
 * schema default does not count, so an untouched intake reads as 0.
 */
export function intakeCompleteness(intake = {}) {
  const total = INTAKE_FIELDS.length;
  let filled = 0;
  for (const f of INTAKE_FIELDS) {
    if (isAnsweredField(f, intake[f.id])) filled += 1;
  }
  return Math.round((filled / total) * 100);
}

/** Which required fields are still blank. */
export function missingRequired(intake = {}) {
  return REQUIRED_INTAKE_FIELDS.filter((id) => {
    const v = intake[id];
    return Array.isArray(v) ? v.length === 0 : String(v ?? "").trim() === "";
  });
}

/**
 * Field-id -> display-value map, for template auto-fill via `placeholder.brandKey`.
 *
 * renderDocTemplate.buildTokenValues() looks values up by the intake FIELD ID declared on each
 * placeholder, not by the token string — so this, not intakeToTokens(), is what you pass as the
 * `profile` argument. Select fields store machine keys, so they are resolved to their human label
 * here (a template should read "Energy drink", not "energy-drink").
 */
export function intakeToProfile(intake = {}) {
  const out = {};
  for (const fieldId of Object.keys(BRAND_TOKEN_MAP)) {
    const v = intake[fieldId];
    if (v === undefined || v === null || String(v).trim() === "") continue;
    const field = INTAKE_FIELD_BY_ID[fieldId];
    if (field?.type === "select") {
      const opt = (field.options || []).find((o) => o.value === v);
      out[fieldId] = opt ? opt.label : String(v);
    } else {
      out[fieldId] = String(v).trim();
    }
  }
  return out;
}

/** Token -> value map. Useful for seeding a form keyed by token; see also intakeToProfile(). */
export function intakeToTokens(intake = {}) {
  const out = {};
  for (const [fieldId, token] of Object.entries(BRAND_TOKEN_MAP)) {
    const v = intake[fieldId];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      const field = INTAKE_FIELD_BY_ID[fieldId];
      // Selects store machine keys; templates need the human label.
      if (field?.type === "select") {
        const opt = (field.options || []).find((o) => o.value === v);
        out[token] = opt ? opt.label : String(v);
      } else {
        out[token] = String(v).trim();
      }
    }
  }
  return out;
}
